import { useCallback, useEffect, useRef } from 'react';

const useLatestRef = (value) => {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
};

const useUnsavedChangesGuard = ({
  enabled,
  saveMessage = 'You have unsaved changes. Save them before leaving?',
  discardMessage = 'Leave without saving these changes?',
  onSave,
  onDiscard,
} = {}) => {
  const enabledRef = useLatestRef(Boolean(enabled));
  const saveMessageRef = useLatestRef(saveMessage);
  const discardMessageRef = useLatestRef(discardMessage);
  const onSaveRef = useLatestRef(onSave);
  const onDiscardRef = useLatestRef(onDiscard);
  const allowNextPopRef = useRef(false);
  const hasInsertedGuardStateRef = useRef(false);

  const confirmNavigation = useCallback(async (leaveAction) => {
    if (!enabledRef.current) {
      if (typeof leaveAction === 'function') {
        await leaveAction();
      }
      return true;
    }

    const shouldSave = window.confirm(saveMessageRef.current);

    if (shouldSave) {
      const saved = await onSaveRef.current?.();

      if (!saved) {
        return false;
      }

      if (typeof leaveAction === 'function') {
        await leaveAction();
      }

      return true;
    }

    const shouldDiscard = window.confirm(discardMessageRef.current);

    if (!shouldDiscard) {
      return false;
    }

    if (typeof onDiscardRef.current === 'function') {
      await onDiscardRef.current();
    }

    if (typeof leaveAction === 'function') {
      await leaveAction();
    }

    return true;
  }, [discardMessageRef, enabledRef, onDiscardRef, onSaveRef, saveMessageRef]);

  useEffect(() => {
    const pushGuardState = () => {
      window.history.pushState(
        { unsavedChangesGuard: true },
        '',
        window.location.href
      );
    };

    if (enabled && !hasInsertedGuardStateRef.current) {
      pushGuardState();
      hasInsertedGuardStateRef.current = true;
    }

    const handlePopState = async () => {
      if (!hasInsertedGuardStateRef.current) {
        return;
      }

      if (allowNextPopRef.current) {
        allowNextPopRef.current = false;
        hasInsertedGuardStateRef.current = false;
        return;
      }

      if (!enabledRef.current) {
        allowNextPopRef.current = true;
        window.history.back();
        return;
      }

      pushGuardState();

      await confirmNavigation(async () => {
        allowNextPopRef.current = true;
        window.history.back();
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [confirmNavigation, enabled, enabledRef]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      if (!enabledRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, enabledRef]);

  return {
    confirmNavigation,
  };
};

export default useUnsavedChangesGuard;
